import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { migrationsApi } from '@/api/migrations';
import type { MigrationDetail, CreateMigrationData, UpdateMigrationData } from '@/types/migration';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, Database, Code, Loader2 } from 'lucide-react';

export function MigrationFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [scriptSQL, setScriptSQL] = useState('');

  useEffect(() => {
    if (id) {
      fetchMigration(parseInt(id));
    }
  }, [id]);

  const fetchMigration = async (migrationId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data: MigrationDetail = await migrationsApi.getOne(migrationId);

      // Check if already executed
      if (data.passed) {
        setError('Cette migration a deja ete executee et ne peut pas etre modifiee');
        navigate(`/migrations/${migrationId}`);
        return;
      }

      setName(data.name);
      setVersion(data.version);
      setScriptSQL(data.scriptSQL);
    } catch (err) {
      setError('Erreur lors du chargement de la migration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Le nom de la migration est requis');
      return;
    }
    if (!version.trim()) {
      setError('La version est requise');
      return;
    }
    if (!scriptSQL.trim()) {
      setError('Le script SQL est requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        const updateData: UpdateMigrationData = {
          name,
          version,
          scriptSQL,
        };
        await migrationsApi.update(parseInt(id), updateData);
        navigate(`/migrations/${id}`);
      } else {
        const createData: CreateMigrationData = {
          name,
          version,
          scriptSQL,
        };
        const result = await migrationsApi.create(createData);
        navigate(`/migrations/${result.id}`);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Erreur lors de la sauvegarde de la migration';
      setError(errorMessage);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to={isEditing ? `/migrations/${id}` : '/migrations'}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {isEditing ? 'Modifier la migration' : 'Nouvelle migration'}
            </h1>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-primary" />
              Informations de la migration
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Ajout colonne email_verified"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="Ex: v2.5.0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-border bg-muted/30">
            <CardTitle className="flex items-center gap-2 text-base">
              <Code className="h-4 w-4 text-primary" />
              Script SQL
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label htmlFor="scriptSQL">Script SQL *</Label>
              <Textarea
                id="scriptSQL"
                value={scriptSQL}
                onChange={(e) => setScriptSQL(e.target.value)}
                placeholder="-- Entrez votre script SQL ici&#10;ALTER TABLE users ADD COLUMN email_verified BOOLEAN DEFAULT FALSE;"
                className="font-mono text-sm min-h-[400px] resize-y"
              />
              <p className="text-xs text-muted-foreground">
                Ce script sera exécuté sur toutes les bases de données des écoles.
                Assurez-vous que le script est idempotent et teste avant de le sauvegarder.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEditing ? `/migrations/${id}` : '/migrations')
            }
            disabled={saving}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Enregistrer
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
